import React, { useState, useEffect, useContext } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useMediaQuery
} from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import CodeIcon from '@mui/icons-material/Code';
import CampaignIcon from '@mui/icons-material/Campaign';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { ColorModeContext } from '../App';
import { useTheme } from '@mui/material/styles';

function Navbar() {
  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Track scroll progress
  useEffect(() => {
    const handleScroll = () => {
      const totalScroll = document.documentElement.scrollHeight - window.innerHeight;
      const currentProgress = (window.scrollY / totalScroll) * 100;
      setScrollProgress(currentProgress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSearch = (event) => {
    if (event.key === 'Enter' && searchQuery.trim()) {
      // Navigate to tracks page with search query
      navigate(`/tracks?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const handleFilterSelect = (filter) => {
    navigate(`/tracks?filter=${filter}`);
    handleFilterClose();
  };

  const menuItems = [
    { label: 'Home', path: '/' },
    { label: 'About', path: '/about' },
    { label: 'Tracks', path: '/tracks' },
    { label: 'Ceremony', path: '/ceremony' },
    { label: 'Volunteers', path: '/volunteer' },
    { label: 'Register', path: '/register' }
  ];

  return (
    <>
    <AppBar 
      position="sticky" 
      sx={{ 
        bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'primary.main',
        boxShadow: 2,
        transition: 'all 0.3s ease'
      }}
    >
      <Toolbar sx={{ minHeight: 64, px: { xs: 1, sm: 2 } }}>
        {/* Mobile Menu Button */}
        {isMobile && (
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => setMobileMenuOpen(true)}
            sx={{ mr: 1 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {/* Logo/Brand */}
        <Box
          component={Link}
          to="/"
          sx={{
            flexGrow: { xs: 1, md: 0 },
            mr: { xs: 0, md: 4 },
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            '&:hover': {
              opacity: 0.8
            }
          }}
        >
          <img 
            src="/logo.png" 
            alt="Lagos Impact Hackathon" 
            style={{
              height: isMobile ? '32px' : '40px',
              width: 'auto',
              objectFit: 'contain'
            }}
          />
        </Box>

        {/* Search Bar - Hidden on mobile, shown on md+ */}
        {!isMobile && (
          <Box sx={{ flexGrow: 1, maxWidth: 400, mr: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search tracks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleSearch}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  color: 'white',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'secondary.main',
                  },
                },
                '& .MuiOutlinedInput-input::placeholder': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  opacity: 1,
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => searchQuery.trim() && navigate(`/tracks?search=${encodeURIComponent(searchQuery.trim())}`)}
                      sx={{ color: 'white' }}
                    >
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )}

        {/* Filter Button - Hidden on mobile */}
        {!isMobile && (
          <IconButton
            color="inherit"
            onClick={handleFilterClick}
            sx={{
              mr: 2,
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <FilterListIcon />
          </IconButton>
        )}

        {/* Dark Mode Toggle */}
        <IconButton 
          onClick={colorMode.toggleColorMode} 
          color="inherit"
          sx={{
            mr: { xs: 0, md: 2 },
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)'
            }
          }}
        >
          {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
        </IconButton>

        {/* Filter Menu */}
        <Menu
          anchorEl={filterAnchorEl}
          open={Boolean(filterAnchorEl)}
          onClose={handleFilterClose}
          PaperProps={{
            sx: { minWidth: 200 }
          }}
        >
          <MenuItem onClick={() => handleFilterSelect('all')}>
            <Typography>All Tracks</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleFilterSelect('tech')}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CodeIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography>Tech for Impact</Typography>
            </Box>
          </MenuItem>
          <MenuItem onClick={() => handleFilterSelect('marketing')}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CampaignIcon sx={{ mr: 1, color: 'secondary.main' }} />
              <Typography>Marketing for Impact</Typography>
            </Box>
          </MenuItem>
        </Menu>

        {/* Navigation Links - Desktop Only */}
        {!isMobile && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              color="inherit"
              component={Link}
              to="/"
              sx={{
                fontWeight: location.pathname === '/' ? 'bold' : 'normal',
                borderBottom: location.pathname === '/' ? '2px solid' : 'none',
                borderColor: 'secondary.main',
                borderRadius: 0,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  borderBottom: '2px solid',
                  borderColor: 'secondary.main'
                }
              }}
            >
              Home
            </Button>
            <Button
              color="inherit"
              component={Link}
              to="/about"
              sx={{
                fontWeight: location.pathname === '/about' ? 'bold' : 'normal',
                borderBottom: location.pathname === '/about' ? '2px solid' : 'none',
                borderColor: 'secondary.main',
                borderRadius: 0,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  borderBottom: '2px solid',
                  borderColor: 'secondary.main'
                }
              }}
            >
              About
            </Button>
            <Button
              color="inherit"
              component={Link}
              to="/tracks"
              sx={{
                fontWeight: location.pathname === '/tracks' ? 'bold' : 'normal',
                borderBottom: location.pathname === '/tracks' ? '2px solid' : 'none',
                borderColor: 'secondary.main',
                borderRadius: 0,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  borderBottom: '2px solid',
                  borderColor: 'secondary.main'
                }
              }}
            >
              Tracks
            </Button>
            <Button
              color="inherit"
              component={Link}
              to="/ceremony"
              sx={{
                fontWeight: location.pathname === '/ceremony' ? 'bold' : 'normal',
                borderBottom: location.pathname === '/ceremony' ? '2px solid' : 'none',
                borderColor: 'secondary.main',
                borderRadius: 0,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  borderBottom: '2px solid',
                  borderColor: 'secondary.main'
                }
              }}
            >
              Ceremony
            </Button>
            <Button
              color="inherit"
              component={Link}
              to="/volunteer"
              sx={{
                fontWeight: location.pathname === '/volunteer' ? 'bold' : 'normal',
                borderBottom: location.pathname === '/volunteer' ? '2px solid' : 'none',
                borderColor: 'secondary.main',
                borderRadius: 0,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.1)',
                  borderBottom: '2px solid',
                  borderColor: 'secondary.main'
                }
              }}
            >
              Volunteers
            </Button>
            <Button
              variant="outlined"
              component={Link}
              to="/register"
              sx={(theme) => ({
                ml: 2,
                color: theme.palette.mode === 'dark' ? theme.palette.text.primary : theme.palette.common.white,
                borderColor: 'secondary.main',
                '&:hover': {
                  bgcolor: 'secondary.main',
                  borderColor: 'secondary.main',
                  color: theme.palette.getContrastText(theme.palette.secondary.main)
                }
              })}
            >
              Register
            </Button>
          </Box>
        )}
      </Toolbar>

      {/* Scroll Progress Bar */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          height: 3,
          width: `${scrollProgress}%`,
          bgcolor: 'secondary.main',
          transition: 'width 0.1s ease',
          boxShadow: '0 0 10px rgba(255, 215, 0, 0.5)'
        }}
      />
    </AppBar>

    {/* Mobile Drawer */}
    <Drawer
      anchor="left"
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      PaperProps={{
        sx: {
          width: 250,
          bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : 'primary.main',
          color: theme.palette.mode === 'dark' ? 'text.primary' : 'common.white'
        }
      }}
    >
      <Box sx={{ pt: 2 }}>
        <IconButton
          onClick={() => setMobileMenuOpen(false)}
          sx={{ 
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'inherit'
          }}
        >
          <CloseIcon />
        </IconButton>
        
        <List>
          {menuItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                component={Link}
                to={item.path}
                selected={location.pathname === item.path}
                onClick={() => setMobileMenuOpen(false)}
                sx={{
                  '&.Mui-selected': {
                    bgcolor: 'rgba(255, 215, 0, 0.2)',
                    borderLeft: '4px solid',
                    borderColor: 'secondary.main',
                    '&:hover': {
                      bgcolor: 'rgba(255, 215, 0, 0.3)'
                    }
                  }
                }}
              >
                <ListItemText primary={item.label} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        {/* Mobile Search */}
        <Box sx={{ px: 2, pb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch(e);
                setMobileMenuOpen(false);
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                color: 'inherit',
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      if (searchQuery.trim()) {
                        navigate(`/tracks?search=${encodeURIComponent(searchQuery.trim())}`);
                        setMobileMenuOpen(false);
                      }
                    }}
                    sx={{ color: 'inherit' }}
                  >
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>
    </Drawer>
    </>
  );
}

export default Navbar;
